<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import PostSaleSalesDetailActions from '../components/PostSaleSalesDetailActions.vue';
import SalesReviewReturnHeader from '../components/SalesReviewReturnHeader.vue';
import SalesTransactionDetailContent from '../components/SalesTransactionDetailContent.vue';
import { usePostSaleSalesDetail } from '../composables/usePostSaleSalesDetail';
import { usePostSaleSalesDetailActions } from '../composables/usePostSaleSalesDetailActions';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const id = computed(() => route.params.postSaleCaseId?.toString() || '');
const detail = usePostSaleSalesDetail({ id, t });
const actions = usePostSaleSalesDetailActions({
  postSaleCase: detail.postSaleCase,
  t,
  notify: useAlert,
});

const dashboardQuery = computed(() => {
  const { detail_tab: _detailTab, ...query } = route.query;
  return query;
});

const backToPostSale = () =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda',
    params: { accountId: route.params.accountId },
    query: dashboardQuery.value,
  });

const openStatus = () =>
  router.push({
    name:
      detail.postSaleCase.value?.status === 'entrada'
        ? 'operacao_imobiliaria_pos_venda_analise'
        : 'operacao_imobiliaria_pos_venda_status',
    params: {
      accountId: route.params.accountId,
      postSaleCaseId: detail.postSaleCase.value.id,
    },
    query: dashboardQuery.value,
  });

const openCorrection = () =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda_devolucao',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: detail.transaction.value.id,
    },
    query: dashboardQuery.value,
  });

const openNucleoRequest = () =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda_pedidos_servico',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: detail.transaction.value.id,
    },
    query: dashboardQuery.value,
  });

const assignResponsible = async responsibleUserId => {
  const assigned = await actions.assign(responsibleUserId);
  if (assigned) await detail.load(id.value);
};

const cancelSale = async ({ reason }) => {
  const cancelled = await actions.cancel(reason);
  if (cancelled) backToPostSale();
};
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <SalesReviewReturnHeader
      v-if="detail.transaction.value"
      read-only
      :transaction="detail.transaction.value"
      @back="backToPostSale"
    />

    <main
      class="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 py-6 sm:px-6 lg:px-10"
    >
      <div
        v-if="detail.loading.value"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.COMMON.LOADING') }}
      </div>
      <section
        v-else-if="detail.error.value"
        class="rounded-lais border border-lais-danger/30 bg-white p-6 text-center"
      >
        <p class="text-sm text-lais-danger">{{ detail.error.value }}</p>
        <LaisPrimaryButton class="mt-4" variant="ghost" @click="backToPostSale">
          {{ t('SALES.ACTIONS.BACK') }}
        </LaisPrimaryButton>
      </section>
      <template
        v-else-if="detail.transaction.value && detail.postSaleCase.value"
      >
        <PostSaleSalesDetailActions
          :agents="actions.agents.value"
          :assigning="actions.assigning.value"
          :cancelling="actions.cancelling.value"
          :case-data="detail.postSaleCase.value"
          :loading-agents="actions.loadingAgents.value"
          @assign="assignResponsible"
          @cancel="cancelSale"
          @correction="openCorrection"
          @nucleo="openNucleoRequest"
          @status="openStatus"
        />

        <SalesTransactionDetailContent
          :initial-tab="route.query.detail_tab || 'sale'"
          :transaction="detail.transaction.value"
        />
      </template>
    </main>
  </LaisGamificationSurface>
</template>
